import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { firstValueFrom, timeout } from 'rxjs';
import { PlaatoSignalRService } from 'app/services/external/plaato-signalr.service';
import { ToastService } from 'app/services/toast.service';
import { PlaatoContentMode } from 'app/models/plaato2';
import { plaatoDeviceId } from 'app/helpers/plaato-device-id';
import * as fromPlaato2 from 'app/state/plaato2';

/**
 * Sets the content mode (beer/CO2) on a Plaato device via the proxy, and mirrors
 * the result to Firestore — PESSIMISTIC:
 *
 *   1. The proxy call is made first (the hardware flag `vw 88`).
 *   2. ONLY on success is `contentMode` written on the plaato2 document.
 *   3. On error a clear error message is shown and NOTHING is saved — otherwise
 *      Firestore would claim a mode the device doesn't have.
 *
 * Returns true if the mode was set (or queued because the device is offline)
 * — the caller should ONLY save the link itself (tap/CO2 bottle ↔ plaato) when
 * this returns true.
 */
@Injectable({
  providedIn: 'root'
})
export class PlaatoContentModeService {

  private signalRService = inject(PlaatoSignalRService);
  private toastService = inject(ToastService);
  private store = inject(Store);

  private readonly requestTimeoutMs = 10_000;

  /** Environment-dependent device-id (prod '54' / stage '00') via the shared
   *  helper — the same source as configure/realtime/setup, so the command hits
   *  the correct device. (NEVER import environment.stage directly; the helper
   *  uses the file-replaced environment.) */
  private toDeviceId(plaato2DocId: string): string {
    return plaatoDeviceId(plaato2DocId);
  }

  async setContentMode(plaato2DocId: string, mode: PlaatoContentMode, uid: string): Promise<boolean> {
    const modeLabel = mode === 'beer' ? 'beer' : 'CO2';

    if (!plaato2DocId) {
      this.toastService.error(`Could not set ${modeLabel} mode: missing Plaato id. The link was not saved.`);
      return false;
    }

    const deviceId = this.toDeviceId(plaato2DocId);
    const modeValue: 1 | 2 = mode === 'beer' ? 1 : 2;

    try {
      const res = await firstValueFrom(
        this.signalRService.setKegContentMode(deviceId, modeValue).pipe(timeout(this.requestTimeoutMs))
      );

      if (!res?.success) {
        this.toastService.error(`Plaato rejected the ${modeLabel} mode change: ${res?.message ?? 'unknown error'}. The link was not saved.`);
        return false;
      }

      // Success (sent directly or queued) → now it's safe to mirror to Firestore.
      this.store.dispatch(fromPlaato2.softUpdate({
        id: plaato2DocId,
        data: { contentMode: mode },
        uid
      }));

      if (res.queued) {
        this.toastService.warning(`Plaato is offline — the ${modeLabel} mode will be applied when it reconnects.`, 5000);
      }

      return true;
    } catch (error) {
      console.error(`Failed to set Plaato content mode (${modeLabel}):`, error);

      // The proxy signals "command failed" with HTTP 4xx/5xx + body
      // ({ success:false, message }), which HttpClient throws. Distinguish it
      // from a true "couldn't reach the proxy" (network error status 0 / timeout)
      // so the message is accurate. Both: pessimistic → no Firestore write.
      if (error instanceof HttpErrorResponse && error.status > 0) {
        const body = error.error as { message?: string } | null;
        const detail = body?.message || error.statusText || `HTTP ${error.status}`;
        this.toastService.error(`Plaato could not apply ${modeLabel} mode: ${detail}. The link was not saved.`);
      } else {
        this.toastService.error(`Could not reach the Plaato server to set ${modeLabel} mode. The link was not saved — please try again.`);
      }
      return false;
    }
  }
}

